<template>
  <v-select
    v-model="selected"
    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
    multiple
    :label="labelKey"
    :disabled="readonly"
    :placeholder="placeholder"
    :options="processedOptions"
    :loading="loading"
    :reduce="(item) => item && reduce ? item[selectionKey] : item"
    :get-option-label="getOptionLabel"
    class="n-async-multi-select"
    :selectable="selectable"
    @search="getOption"
    @open="open"
  >
    <template #option="option">
      <div
        v-if="option.isGroupLabel"
        class="group-label"
      >
        {{ formatLabel(option) }}
      </div>
      <span v-else>{{ formatLabel(option) }}</span>
    </template>
    <template
      v-for="(_, slot) of $scopedSlots"
      v-slot:[slot]="scope"
    >
      <slot
        :name="slot"
        v-bind="scope"
      />
    </template>
    <template #no-options>
      {{ $t("selection.noOption") }}
    </template>
    <template #spinner="{ loading }">
      <b-spinner
        v-if="loading"
        variant="primary"
        label="Spinning"
      />
    </template>
  </v-select>
</template>

<script>
import vSelect from 'vue-select'
import { BSpinner } from 'bootstrap-vue'

import RepositoryFactory from '@/repositories/RepositoryFactory'

export default {
  components: {
    vSelect,
    BSpinner,
  },
  props: {
    placeholder: {},
    name: {},
    disabled: {},
    readonly: {},
    repository: {},
    selectionKey: {
      type: String,
      default: 'id',
    },
    selectionLabel: {
      type: [String, Array],
      default: 'name',
    },
    labelSeparator: {
      type: String,
      default: ' ',
    },
    labelWrapper: {
      type: Object,
      default: () => ({
        start: '(',
        end: ')',
      }),
    },
    initOptions: {
      type: Array,
      default() {
        return []
      },
    },
    reduce: {
      type: Boolean,
      default: true,
    },
    query: {
      type: Object,
      default() {
        return {}
      },
    },
    isCustomParams: {
      type: Boolean,
      default: false,
    },
    dependencyKey: {
      type: String,
      default: '',
    },
    dependencyValue: {
      type: String,
      default: '',
    },
    isObject: {
      type: Boolean,
      default: false,
    },
    alwaysFresh: {
      type: Boolean,
      default: false,
    },
    includedAll: {
      type: Boolean,
      default: false,
    },
    allOptionValue: {
      type: String,
      default: 'all',
    },
    noExcluded: {
      type: Boolean,
      default: false,
    },
    spreadAll: {
      type: Boolean,
      default: false,
    },
    mustExcludedIds: {
      type: Array,
      default: () => [],
    },
    selectable: {
      type: Function,
      default: option => !option?.isGroupLabel,
    },
    groupKey: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      option: [],
      selected: [],
      Repository: null,
      loading: false,
      loadedInitOptions: false,
    }
  },
  computed: {
    labelKey() {
      return this.selectionLabel === 'translations' ? 'translations' : 'name'
    },
    processedOptions() {
      // If no groupKey is provided, return options as is
      if (!this.groupKey || !this.option.length) {
        return this.option
      }

      // Create a map to group options by the groupKey
      const groups = {}
      const result = []

      // Group options by the groupKey
      this.option.forEach(item => {
        // Skip items that don't have the groupKey
        if (!item[this.groupKey]) {
          result.push(item)
          return
        }

        // Extract the group object based on the groupKey
        const groupObj = item[this.groupKey]

        // Get group identifier (use code for service objects)
        const groupId = groupObj.code || groupObj.id || groupObj

        // Extract the group display name from the object
        let groupName
        if (groupObj.name) {
          if (typeof groupObj.name === 'object') {
            const locale = this.$i18n.locale.split('-')[0]
            groupName = groupObj.name[locale] || groupObj.name.en || groupObj.name.zh
                      || groupObj.name.km || groupObj.name.vi || groupId
          } else {
            groupName = groupObj.name
          }
        } else {
          groupName = groupId
        }

        // Check if we've already added this group label
        if (!groups[groupId]) {
          // Create group label item (non-selectable)
          const groupLabel = {
            [this.selectionKey]: `group_${groupId}`,
            [this.selectionLabel]: groupName,
            isGroupLabel: true,
            selectable: false,
          }

          result.push(groupLabel)
          groups[groupId] = true
        }

        // Add the item to the result array
        result.push(item)
      })

      return result
    },
  },
  watch: {
    selected: {
      handler(newVal, oldVal) {
        // Fallback if oldVal is not an array
        let prevVal = oldVal
        if (!Array.isArray(prevVal)) {
          prevVal = []
        }

        // Make sure we're working with arrays
        if (!Array.isArray(newVal)) return

        // Check for the "All" option handling
        if (this.includedAll && this.allOptionValue) {
          // Old selection had "All"?
          const hadAll = prevVal.includes(this.allOptionValue)
          // New selection has "All"?
          const hasAll = newVal.includes(this.allOptionValue)

          // Scenario 1: "All" was just added
          if (hasAll && !hadAll) {
            // Clear all other items, keep "All"
            this.$nextTick(() => {
              if (this.spreadAll) {
                // Select all available options except the "All" option
                const allOptions = this.option
                  .filter(opt => opt[this.selectionKey] !== this.allOptionValue)
                  .map(opt => (this.reduce ? opt[this.selectionKey] : opt))

                this.selected = allOptions
                const selectedObjects = allOptions.map(val => {
                  const id = this.reduce ? val : val[this.selectionKey]
                  return this.option.find(opt => opt[this.selectionKey] === id)
                })
                this.$emit('input', this.isObject ? selectedObjects : allOptions)
                this.$emit('change', selectedObjects)
              } else {
              // Original behavior - just select "All"
                this.selected = [this.allOptionValue]
                // Use getAllOption to get "All" with a unique key
                const allOpt = this.getAllOption()
                this.$emit('input', this.isObject ? [allOpt] : [this.allOptionValue])
                this.$emit('change', [allOpt])
              }
            })
            return
          }

          // Scenario 2: "All" was selected, but user picked something else
          if (hasAll && hadAll && newVal.length > 1) {
            // Remove "All" and keep the others
            const filteredValues = newVal.filter(v => v !== this.allOptionValue)
            this.$nextTick(() => {
              this.selected = filteredValues
              const selectedObjects = filteredValues.map(val => {
                const id = this.reduce ? val : val[this.selectionKey]
                return this.option.find(opt => opt[this.selectionKey] === id)
              })
              const validSelectedObjects = this.isObject ? selectedObjects : filteredValues
              this.$emit('input', validSelectedObjects)
              this.$emit('change', selectedObjects)
            })
            return
          }
        }

        // Standard validation for non-"All" selections
        const validSelected = newVal.filter(val => {
          const id = this.reduce ? val : val[this.selectionKey]
          return this.option.some(opt => opt[this.selectionKey] === id)
        })

        if (validSelected.length !== newVal.length) {
          this.selected = validSelected
        }

        // Emit events
        const selectedObjects = validSelected.map(val => {
          const id = this.reduce ? val : val[this.selectionKey]
          return this.option.find(opt => opt[this.selectionKey] === id)
        })
        const validSelectedObjects = this.isObject ? selectedObjects : validSelected
        // console.log('validSelectedObjects', validSelectedObjects)
        // console.log('selectedObjects', selectedObjects)

        this.$emit('input', validSelectedObjects)
        this.$emit('change', selectedObjects)
      },
      // immediate: true,
    },
    initOptions(value) {
      if (!this.loadedInitOptions && value.length) {
        this.getOption()
      }
    },
    '$i18n.locale': {
      handler() {
        if (this.selectionLabel === 'translations' || this.includedAll) {
          this.updateTranslations()
        }

        if (this.includedAll) {
          this.getOption()
        }
      },
      // immediate: true,
    },
  },
  created() {
    this.Repository = RepositoryFactory.get(this.repository)
    if (this.initOptions.length && this.alwaysFresh) {
      this.getOption()
    }
  },
  methods: {
    getAllOption() {
      // Return actual "All" object if it exists, otherwise create one
      return (
        this.option.find(opt => opt[this.selectionKey] === this.allOptionValue) || {
          [this.selectionKey]: this.allOptionValue,
          [this.selectionLabel]: this.$t('general.all'),
        }
      )
    },
    formatLabel(item) {
      if (!item) return ''

      // Special handling for group labels
      if (item.isGroupLabel) {
        // Handle both string and object name formats for group labels
        const label = item[this.selectionLabel]
        if (typeof label === 'object') {
          const locale = this.$i18n.locale.split('-')[0]
          return label[locale] || label.en || label.zh || label.km || label.vi || '-'
        }
        return label
      }

      if (Array.isArray(this.selectionLabel)) {
        return this.selectionLabel.map((label, index) => {
          const value = this.getTranslatedValue(item, label)
          if (index === 0) return value
          return `${this.labelWrapper.start}${value}${this.labelWrapper.end}`
        }).join(this.labelSeparator)
      }
      return item[this.selectionLabel]
    },
    getTranslatedValue(item, label) {
      const fieldValue = item?.[label]
      if (!fieldValue) return ''

      if (typeof fieldValue !== 'object') return fieldValue

      if (typeof fieldValue.name !== 'object') return fieldValue.name

      const locale = this.$i18n.locale.split('-')[0]
      // console.log('fieldValue.name', fieldValue.name)
      return fieldValue.name[locale]
        || fieldValue.name.en
        || fieldValue.name.zh
        || fieldValue.name.km
        || fieldValue.name.vi
        || '-'
    },
    getOptionLabel(option) {
      if (!option || typeof option !== 'object') return null
      const label = this.formatLabel(option)
      return label || null
    },
    clear() {
      this.option = []
      this.selected = []
      this.$emit('input', [])
    },
    open() {
      this.getOption()
    },
    mapExcludeIds() {
      const excludeIds = [...this.option]
      return excludeIds.map(item => item[this.selectionKey])
    },
    mergeArray(arrayA, arrayB) {
      return arrayA.concat(arrayB).reduce((accumulator, currentValue) => {
        if (!accumulator.some(item => item[this.selectionKey] === currentValue[this.selectionKey])) {
          accumulator.push(currentValue)
        }
        return accumulator
      }, [])
    },
    updateTranslations() {
      if (!this.option.length) return

      const { locale } = this.$i18n
      const normalizedLocale = locale === 'zh-cn' ? 'zh' : locale

      this.option = this.option.map(item => ({
        ...item,
        translations: item?.translationsObj?.[normalizedLocale]
          || item?.translationsObj?.en
          || item?.translationsObj?.zh
          || item?.translationsObj?.km
          || item?.translationsObj?.vi
          || '-',
        translationsObj: item?.translationsObj,
      }))
    },
    getOption(search = null) {
      this.loading = true
      const { locale } = this.$i18n
      const needTranslate = this.selectionLabel === 'translations'
      const normalizedLocale = locale === 'zh-cn' ? 'zh' : locale
      const excludeIds = this.mapExcludeIds()

      this.Repository.options({
        limit: 10,
        offset: 0,
        search: search || '',
        includeIds: this.loadedInitOptions ? '' : this.initOptions.join(','),
        excludeIds: this.loadedInitOptions ? excludeIds.join(',') : '',
        ...this.query,
        ...(this.dependencyKey && this.dependencyValue
          ? { [this.dependencyKey]: this.dependencyValue }
          : {}),
        ...(this.isCustomParams && this.$route.params.departmentId
          ? { departmentId: this.$route.params.departmentId }
          : {}),
        ...(this.noExcluded ? { excludeIds: '' } : {}),
        ...(this.mustExcludedIds.length ? { excludeIds: this.mustExcludedIds.join(',') } : {}),
      })
        .then(response => {
          let list = Array.isArray(response?.data?.data?.list)
            ? response?.data?.data?.list
            : response.data.data

          if (needTranslate) {
            list = list.map(e => ({
              ...e,
              translations:
                e?.translation?.name?.[normalizedLocale]
                || e?.translation?.name?.en
                || e?.translation?.name?.zh
                || e?.translation?.name?.km
                || e?.translation?.name?.vi
                || e?.translations?.[normalizedLocale]
                || e?.translations?.en
                || e?.translations?.zh
                || e?.translations?.km
                || e?.translations?.vi
                || '-',
              translationsObj: e?.translations || e?.translation?.name,
            }))
          }

          if (this.alwaysFresh) {
            this.option = list
          } else {
            this.option = this.mergeArray(this.option, list)
          }

          if (this.includedAll) {
            this.option.unshift({
              [this.selectionKey]: this.allOptionValue,
              [this.selectionLabel]: this.$t('general.all'),
            })
          }

          if (!this.loadedInitOptions) {
            if (this.initOptions) {
              this.selected = this.initOptions
            }
            this.loadedInitOptions = true
          }
        })
        .catch()
        .then(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.n-async-multi-select {
  .vs__deselect {
    fill: white;
    color: white;
    svg {
      fill: white;
      color: white;
    }
  }
  .vs__selected .vs__deselect {
    fill: white;
    color: white;
  }
  .vs__actions {
    .vs__clear {
      fill: white;
      color: white;
    }
  }
  // Group label styling
  .group-label {
    font-size: 1rem;
    font-weight: 500;
    color: #7f7f7f;
    padding: 10px 10px 4px 10px;
    margin-top: 0;
    margin-bottom: 0;
    // background-color: #f0f0f0;
    pointer-events: none;
    user-select: none;
    margin-left: -18px;
    border-top: 1px solid #e0e0e0;
  }
}
</style>
